import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CheckoutRefused,
  CustomerCartNotFound,
  ErrorCode,
  InvalidFormField,
  PaymentsNotReady,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
  WrongCommerceMode,
} from "../errors";
import { parse } from "../model/common";
import { StoreConnection } from "../model/store";
import { ProviderConnect, TerminalCheckout, TerminalConnectionToken } from "../model/stripe";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

/**
 * `/providers/stripe`: the routes that are Stripe by definition. Connection
 * state and disconnecting go through `StoresApi.provider*` with key `stripe`.
 *
 * A refusal from the store's Stripe account (onboarding unfinished, no active
 * account) arrives as a bare `{error}` and becomes `PaymentsNotReady`.
 */
export class StripeApi {
  constructor(private readonly t: Transport) {}

  /** Starts hosted onboarding for a store and returns where to send the merchant. Repeatable: a store mid-onboarding gets a fresh link. */
  async connect(
    ctx: ApiContext,
    storeId: string,
  ): Promise<
    Result<
      ProviderConnect,
      ApiNotAuthorized | StoreNotFound | PaymentsNotReady | StoreRoleForbidden | WrongCommerceMode
    >
  > {
    const r = await this.t.post(ctx, "/providers/stripe/connect", { body: { storeId } });
    return forward(
      r,
      (j) => parse(ProviderConnect, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.WrongCommerceMode)
            return new WrongCommerceMode(e.incorrectValue as "SHOPIFY" | "STRIPE", e.detailedDesc, e);
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Test mode only: points the store at an account already connected to the platform. */
  async attach(
    ctx: ApiContext,
    storeId: string,
    accountId: string,
  ): Promise<
    Result<
      StoreConnection,
      ApiNotAuthorized | StoreNotFound | InvalidFormField | PaymentsNotReady | StoreRoleForbidden | WrongCommerceMode
    >
  > {
    const r = await this.t.post(ctx, "/providers/stripe/attach", { body: { storeId, accountId } });
    return forward(
      r,
      (j) => parse(StoreConnection, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.WrongCommerceMode)
            return new WrongCommerceMode(e.incorrectValue as "SHOPIFY" | "STRIPE", e.detailedDesc, e);
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** A link into the store's own Stripe dashboard. Single use for older accounts: never cache it. */
  async dashboard(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<ProviderConnect, ApiNotAuthorized | StoreNotFound | PaymentsNotReady | StoreRoleForbidden>> {
    const r = await this.t.post(ctx, "/providers/stripe/dashboard", { body: { storeId } });
    return forward(
      r,
      (j) => parse(ProviderConnect, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** A Terminal SDK connection token on the store's account, scoped to a branch when one is named. */
  async connectionToken(
    ctx: ApiContext,
    storeId: string,
    locationId?: string,
  ): Promise<Result<TerminalConnectionToken, ApiNotAuthorized | StoreNotFound | PaymentsNotReady | WrongCommerceMode>> {
    const r = await this.t.post(ctx, "/providers/stripe/pos/connection-token", { body: { storeId, locationId } });
    return forward(
      r,
      (j) => parse(TerminalConnectionToken, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.WrongCommerceMode)
            return new WrongCommerceMode(e.incorrectValue as "SHOPIFY" | "STRIPE", e.detailedDesc, e);
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Prepares an in-person payment for a cart at a store the caller owns: a connection token and a card-present PaymentIntent. */
  async posCheckout(
    ctx: ApiContext,
    customerCartId: string,
    options: { pushToken?: string } = {},
  ): Promise<
    Result<
      TerminalCheckout,
      ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | PaymentsNotReady | WrongCommerceMode
    >
  > {
    const r = await this.t.post(ctx, `/providers/stripe/pos/checkout/customer-cart/${customerCartId}`, {
      body: options,
    });
    return forward(
      r,
      (j) => parse(TerminalCheckout, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.code === ErrorCode.WrongCommerceMode)
            return new WrongCommerceMode(e.incorrectValue as "SHOPIFY" | "STRIPE", e.detailedDesc, e);
          if (e.incorrectValue === "id") return new CustomerCartNotFound(customerCartId, e);
          if (e.incorrectValue === "items" || e.incorrectValue === "status")
            return new CheckoutRefused(e.detailedDesc, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
